import React, { useEffect, useState, useMemo } from "react";
import { Tooltip } from "flowbite-react";
import { HiQuestionMarkCircle, HiExclamation } from "react-icons/hi";
import { useSelector } from 'react-redux';
import { useProjectService, useResourcesService, useRoadmapService } from '../../services';
import { notify } from "react-notify-toast";
import { Drawer } from "../../components/Modals";
import CustomTooltip from '../../components/common/CustomTooltip';

const SkillsModal = ({
  showModal,
  setShowModal,
  roadmapDetails,
  sim_flag,
  handleRefreshClick,
  setSimulationToggle,
  readOnly = false,
}: any) => {
  const { getMappedResources, saveMappedResources } = useProjectService();
  const { fetchData: fetchGanttData } = useRoadmapService();
  const { getResourcesbySkill } = useResourcesService();

  const [toggle, setToggle] = useState(sim_flag);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(-1);
  const [availableMembers, setAvailableMembers] = useState([] as any[]);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [highlightSim, setHighlightSim] = useState(false);
  const [pingActive, setPingActive] = useState(false);
  const [simulationActivatedInModal, setSimulationActivatedInModal] = useState(false);
  const pingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [mappedResource, SetmappedResources] = useState({} as any);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [assignmentError, setAssignmentError] = useState<{
    skillId: string | number;
    memberId: string | number;
    taskNames: string[];
  } | null>(null);

  const {
    mappedResources,
    mappedResourcesLoading,
    saveMappedResourcesLoading,
  } = useSelector((state: any) => state.projects);

  const {
    loading,
    taskAllocated,
    resources_used_in_tasks,
    data: roadmapData
  } = useSelector((state: any) => state.roadmap);

  const {
    skill_resources_Loading,
    skill_resources,
    data: allResources
  } = useSelector((state: any) => state.resources);

  // Handle mappedResources from Redux - exactly matches index.tsx pattern
  useEffect(() => {
    if (Object.keys(mappedResources).length) {
      SetmappedResources(mappedResources);
    }
  }, [mappedResources]);

  const filteredAvailableMembers = useMemo(() => {
    if (!memberSearchQuery.trim()) return availableMembers;

    return availableMembers.filter((member: any) => {
      const searchTerm = memberSearchQuery.toLowerCase();
      const name = member?.name?.toLowerCase() || '';
      const role = member?.role?.toLowerCase() || '';

      return name.includes(searchTerm) || role.includes(searchTerm);
    });
  }, [availableMembers, memberSearchQuery]);

  const simHeaderBg = (!toggle || !sim_flag) ? `bg-[#5D99D0] text-white` : "bg-[#568B77] text-white";

  const handleOnDurationChange = async (value: any) => {
    SetmappedResources({});
    if (value) {
      await fetchGanttData(roadmapDetails.project_id, sim_flag, { duration_limit: value }, true);
    } else if (roadmapDetails?.durations?.length) {
      const val = Math.min(...roadmapDetails.durations);
      await fetchGanttData(roadmapDetails.project_id, sim_flag, { duration_limit: val }, true);
    }

    window.dispatchEvent(new CustomEvent('capacity-planning:refresh'));
  };

  const handleAddMemberClick = (skill: any, buttonIndex: number) => {
    if (readOnly) return;
    setSelectedSkill(skill);
    setSelectedButtonIndex(buttonIndex);
    setShowMemberDropdown(true);
    setMemberSearchQuery(''); // Clear search when opening dropdown

    // Calculate dropdown position based on available space
    setTimeout(() => {
      const button = document.querySelector(`[data-skill-id="${skill}"] .add-member-btn[data-button-index="${buttonIndex}"]`);
      if (button) {
        const rect = (button as HTMLElement).getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 320; // Approximate dropdown height
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Show above if not enough space below and there's more space above
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    }, 0);

    // Fetch available members for this skill from API
    if (skill && !skill_resources_Loading) {
      getResourcesbySkill(skill);
    }
  };

  const handleCloseMemberDropdown = () => {
    setSelectedSkill('');
    setSelectedButtonIndex(-1);
    setShowMemberDropdown(false);
    setAvailableMembers([]);
    setMemberSearchQuery(''); // Clear search when closing dropdown
  };

  const handleSelectMember = (member: any) => {
    const skillId = selectedSkill;
    const mappedResourceObj: any = {
      ...mappedResource,
      [skillId]: {
        ...(mappedResource[skillId] || { skill_id: skillId, members: [] }),
        members: [...(mappedResource[skillId]?.members || [])]
      }
    };

    // Check if member is already assigned
    const isAlreadyAssigned = mappedResourceObj[skillId].members.some((m: any) => m.id === member.id);

    if (!isAlreadyAssigned) {
      mappedResourceObj[skillId].members.push(member);

      SetmappedResources(mappedResourceObj);
    }

    handleCloseMemberDropdown();
  };

  const handleMemberRemove = (member: any, skill_id: any) => {
    const mappedResourceObj: any = { ...mappedResource };
    const taskAllocatedData = { ...taskAllocated };
    const skillData = mappedResourceObj[skill_id];
    const allocatedmemberData = taskAllocatedData[member.id] || "";

    if (allocatedmemberData) {
      const skilledtask = allocatedmemberData[skill_id] || "";
      if (skilledtask.length) {
        notify.show(`${member.name} has at least one task assigned on this project, please remove and try again.`, "error", 3000);
        return;
      }
    }

    if (resources_used_in_tasks && resources_used_in_tasks[member.id] && resources_used_in_tasks[member.id][skill_id]) {
      const taskIds = resources_used_in_tasks[member.id][skill_id];
      if (taskIds && taskIds.length > 0) {
        // Find task names using roadmapData (which contains [projectRow, ...tasks])
        let taskNames: string[] = [];

        if (roadmapData && Array.isArray(roadmapData)) {
          taskNames = roadmapData
            .filter((item: any) => taskIds.includes(item.id))
            .map((item: any) => `[${item.id}] ${item.name}`);
        }

        setAssignmentError({
          skillId: skill_id,
          memberId: member.id,
          taskNames: taskNames
        });
        return;
      }
    }

    const skillDataArr = skillData.members.filter((arr: any) => arr.id !== member.id);
    mappedResourceObj[skill_id] = {
      ...mappedResourceObj[skill_id],
      members: skillDataArr
    };
    SetmappedResources(mappedResourceObj);
  };

  const handleSaveMemberClick = async () => {
    let membersIds: any = [];
    let skill_members: any = {};
    const mappedResourceObj: any = { ...mappedResource };
    const keys = Object.keys(mappedResourceObj);

    if (keys.length) {
      keys.forEach(key => {
        const { members = [], skill_id = '' } = mappedResourceObj[key];
        let membersId: any = [];
        if (members.length) {
          members.forEach((ele: any) => {
            membersIds.push(ele.id);
            membersId.push(ele.id);
          });
          // Ensure we key by skill id string, not name
          skill_members[String(key)] = Array.from(new Set(membersId));
        }
      });
    }

    if (!saveMappedResourcesLoading && roadmapDetails?.project_id) {
      await saveMappedResources(roadmapDetails.project_id, { members: membersIds, skill_members });
    }

    handleRefreshClick();
  };

  const handleSaveAndTurnOff = () => {
    // Save first
    handleSaveMemberClick();
    // Turn off simulation UI locally
    if (toggle) {
      setToggle(false);
      setSimulationToggle(false);
      setSimulationActivatedInModal(false);
    }
  };

  const handleMapMembersClick = () => {
    const skill_limit = roadmapDetails.skills_required?.reduce((acc: any, _cur: any) => {
      return { ...acc, [_cur.name]: _cur.count };
    }, {});

    if (!mappedResourcesLoading && roadmapDetails?.project_id) {
      getMappedResources(roadmapDetails.project_id, {
        "skill_limit": {
          ...skill_limit
        },
        sim_flag
      });
    }
  };

  // Handle API response for skill resources - matches SingleResourceModal pattern
  useEffect(() => {
    if (skill_resources && skill_resources.length && selectedSkill && showMemberDropdown) {
      // Merge existing mapped resources with available resources like SingleResourceModal does
      const mappedResourceObj: any = { ...mappedResource };
      const skillData = mappedResourceObj[selectedSkill];

      if (skillData && skillData.members) {
        // Combine API resources with already selected members
        const combinedResources = [...skill_resources, ...skillData.members];
        setAvailableMembers(combinedResources);
      } else {
        setAvailableMembers(skill_resources);
      }
    }
  }, [skill_resources, selectedSkill, showMemberDropdown, mappedResource]);


  // Build mappedResource structure from skill_members using already-loaded org resources
  useEffect(() => {
    const skillMembersObj = roadmapDetails?.skill_members;
    if (!skillMembersObj || !Array.isArray(roadmapDetails?.skills_required) || !allResources?.length) return;

    const initialMappedResource: any = {};

    roadmapDetails.skills_required.forEach((skill: any) => {
      const skillIdStr = String(skill.id);
      const memberIdsForSkill: any[] = skillMembersObj?.[skillIdStr] || [];
      const membersDetailed = memberIdsForSkill
        .map((memberId: any) => allResources
          .filter((r: any) => !r.is_deleted)
          .find((r: any) => String(r.id) === String(memberId))
        )
        .filter(Boolean);

      initialMappedResource[skill.id] = {
        skill_id: skill.id,
        skill_name: skill.name,
        members: membersDetailed,
      };
    });

    SetmappedResources(initialMappedResource);
  }, [allResources, roadmapDetails?.skill_members, roadmapDetails?.skills_required]);

  // Initialize empty structure for skills if needed (only when nothing else populated it)
  useEffect(() => {
    if (roadmapDetails.skills_required && (!roadmapDetails.members_data || Array.isArray(roadmapDetails.members_data))) {
      // If mappedResource already has the proper structure, don't reinitialize
      const hasProperStructure = Object.keys(mappedResource).length > 0 &&
        !Array.isArray(mappedResource) &&
        Object.values(mappedResource).some((v: any) => v.hasOwnProperty('members'));

      if (hasProperStructure) {
        return;
      }

      const initialMappedResource: any = {};
      roadmapDetails.skills_required.forEach((skill: any) => {
        initialMappedResource[skill.id] = {
          skill_id: skill.id,
          skill_name: skill.name,
          members: []
        };
      });
      if (Object.keys(initialMappedResource).length > 0) {
        SetmappedResources(initialMappedResource);
      }
    }
  }, [roadmapDetails.skills_required, mappedResource]);

  // Handle clicking outside popover to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assignmentError) {
        const target = event.target as Element;
        if (!target.closest('.assignment-error-popover')) {
          setAssignmentError(null);
        }
      }
    };

    if (assignmentError) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [assignmentError]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMemberDropdown) {
        const target = event.target as Element;
        // Check if click is outside dropdown
        if (!target.closest('.member-dropdown') && !target.closest('.add-member-btn')) {
          handleCloseMemberDropdown();
        }
      }
    };

    if (showMemberDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMemberDropdown]);

  // Reset highlight once simulation is turned on
  useEffect(() => {
    if (toggle) {
      setHighlightSim(false);
    }
  }, [toggle]);

  useEffect(() => {
    return () => {
      if (pingTimeoutRef.current) clearTimeout(pingTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <Drawer
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        position="right"
      >
        <div className="relative h-full overflow-hidden flex flex-col bg-white dark:bg-gray-900">
          {/* Header */}
          <div className={`sticky top-0 left-0 flex flex-row border-b-2 rounded-b-2xl border-slate-500 dark:border-slate-700 ${simHeaderBg} shadow-xl z-10`}>
            <div className="flex w-full items-center px-6 py-7">
              <div className="flex-1 w-[25%] mr-4">
                <h2 className="text-white text-lg mb-1 font-semibold whitespace-nowrap  truncate" title={roadmapDetails.project_name}>
                  {roadmapDetails.project_name}
                </h2>
                <p className="text-white/70 text-sm">People Management</p>
              </div>

              <div className="flex items-center gap-3 mr-2">
                {readOnly && (
                  <CustomTooltip
                    title="You're viewing a template, so editing is disabled."
                    placement="bottom"
                  >
                    <div className="bg-blue-500 dark:bg-blue-600 text-white dark:text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg border border-blue-600 dark:border-blue-500 cursor-help hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors duration-200">
                      Read-only mode
                    </div>
                  </CustomTooltip>
                )}
                {!readOnly && (
                  <>
                    <span className="text-sm text-white font-semibold whitespace-nowrap">
                      {toggle ? "Simulation" : "Live"}
                    </span>

                    {/* Minimal Toggle Switch - matches BreadcrumbHeader */}
                    <div
                      className={`
                        relative w-12 h-7 rounded-full p-0.5 transition-all duration-300 ease-in-out 
                        ${toggle ? "bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-lg " : "bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg "} 
                        ${!loading && 'cursor-pointer hover:scale-105 active:scale-95'} ${highlightSim && !toggle ? 'ring-1 ring-yellow-300 shadow-2xl' : ''}
                      `}
                      onClick={() => {
                        if (!loading) {
                          const newVal = !toggle;
                          setToggle(newVal);
                          setSimulationToggle(newVal);
                          // Track if user manually turned simulation ON inside modal
                          if (!toggle && newVal) {
                            setSimulationActivatedInModal(true);
                          }
                        }
                      }}
                    >
                      {/* Glowing overlay */}
                      {highlightSim && pingActive && !toggle && (
                        <span className="absolute inset-0 rounded-full ring-2 ring-yellow-300 animate-ping pointer-events-none" />
                      )}
                      {/* Toggle Circle */}
                      <div
                        className={`
                          w-6 h-6 bg-white rounded-full shadow-md transform transition-all duration-300 ease-in-out flex items-center justify-center 
                          ${toggle ? "translate-x-5" : "translate-x-0"}
                        `}
                      >
                        {/* Minimal Status Dot */}
                        <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${toggle ? "bg-emerald-500" : "bg-blue-500"}`} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                type="button"
                className="ml-2 text-white/80 hover:text-white w-8 h-8 rounded-lg hover:bg-white/10 transition-all duration-200 flex items-center justify-center"
                onClick={() => setShowModal(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {loading ? (
              <div className="p-6">
                {/* Top Stats Section Skeleton */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[1, 2, 3, 4].map((index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
                        <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                      </div>
                      <div className="h-7 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-24" />
                    </div>
                  ))}
                </div>

                {/* Project Timeline Section Skeleton */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-32" />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3].map((index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-16 mb-2" />
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-24" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resource Allocation Section Skeleton */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gray-300 dark:bg-gray-600 animate-pulse" />
                      <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-48" />
                    </div>
                    {sim_flag && (
                      <div className="flex gap-3">
                        <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse w-24" />
                        <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse w-20" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                        <div className="flex flex-col gap-4">
                          {/* Header Row Skeleton */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gray-300 dark:bg-gray-600 animate-pulse" />
                              <div>
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-24 mb-2" />
                                <div className="flex items-center gap-3">
                                  <div className="w-20 bg-gray-300 dark:bg-gray-700 rounded-full h-1.5" />
                                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-8" />
                                  <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse w-20" />
                                </div>
                              </div>
                            </div>
                            {sim_flag && (
                              <div className="h-9 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse w-28" />
                            )}
                          </div>

                          {/* Members Section Skeleton */}
                          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-300 dark:border-gray-700">
                            {[1, 2, 3, 4, 5].map((memberIndex) => (
                              <div key={memberIndex} className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded-lg px-3 py-2">
                                <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
                                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6">
                {/* Top Stats Section */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-700 dark:text-gray-400 font-medium">Work Unit Duration</span>
                      <svg className="w-4 h-4 text-primary-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {roadmapDetails?.duration_with_delay || '-'} days
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-700 dark:text-gray-400 font-medium">Total Skills</span>
                      <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {roadmapDetails?.skills_required?.length || 0}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-700 dark:text-gray-400 font-medium">People Required</span>
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {roadmapDetails?.skills_required?.reduce((acc: number, skill: any) => acc + skill.count, 0) || 0}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-700 dark:text-gray-400 font-medium">Assigned</span>
                      <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {Object.values(mappedResource).reduce((acc: number, res: any) => acc + (res.members?.length || 0), 0)}
                    </div>
                  </div>
                </div>

                {/* Project Timeline Section - Compact */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-gray-900 dark:text-white text-base font-semibold mb-3">
                    Work Unit Timeline
                  </h3>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                      <label className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1 block">Start Date</label>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {roadmapDetails?.start_date ? new Date(roadmapDetails?.start_date).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                      <label className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1 block">End Date</label>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {roadmapDetails?.end_date ? new Date(roadmapDetails?.end_date).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-gray-600 dark:text-gray-400 font-medium">Duration</label>
                        {roadmapDetails.is_sim_pending && (
                          <Tooltip placement="top" content="Merge/Reset for Cadence people suggestions.">
                            <HiQuestionMarkCircle className="text-xs text-gray-400 cursor-help" />
                          </Tooltip>
                        )}
                      </div>
                      <select
                        className="w-full rounded-md bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-900 dark:text-white text-sm font-medium focus:ring-1 focus:ring-blue-500 p-1.5 cursor-pointer transition-all duration-200"
                        onChange={(e) => handleOnDurationChange(e.target.value)}
                        disabled={readOnly || roadmapDetails.is_sim_pending || !sim_flag}
                      >
                        <option value="">Select</option>
                        {roadmapDetails?.durations?.map((duration: any, index: number) => (
                          <option value={duration} key={`${duration}_${index}`}>
                            {duration} days
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Resource Allocation Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700 relative group"
                  onMouseEnter={() => {
                    if (!toggle && !readOnly) {
                      setHighlightSim(true);
                      setPingActive(true);
                      if (pingTimeoutRef.current) clearTimeout(pingTimeoutRef.current);
                      pingTimeoutRef.current = setTimeout(() => {
                        setPingActive(false);
                      }, 5000);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!readOnly) {
                      setHighlightSim(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-gray-900 dark:text-white text-lg font-semibold">
                      People Allocation by Skill
                    </h3>

                    {toggle && !readOnly && (
                      <div className="flex gap-3 flex-wrap">
                        <button
                          className={`px-4 py-2 text-sm ${mappedResourcesLoading
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                            : "bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600"
                            } rounded-lg transition-colors duration-200 font-medium`}
                          onClick={handleMapMembersClick}
                          disabled={mappedResourcesLoading}
                        >
                          Auto-Map
                        </button>
                        <button
                          className={`px-4 py-2 text-sm ${saveMappedResourcesLoading
                            ? "bg-blue-500/50 text-white cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                            } rounded-lg transition-colors duration-200 font-medium`}
                          onClick={handleSaveMemberClick}
                          disabled={saveMappedResourcesLoading}
                        >
                          Save All
                        </button>
                        {simulationActivatedInModal && (
                          <button
                            className={`px-4 py-2 text-sm ${saveMappedResourcesLoading
                              ? "bg-[#568B77] text-white cursor-not-allowed"
                              : "bg-[#568B77] hover:bg-green-700/90 text-white"
                              } rounded-lg transition-colors duration-200 font-medium`}
                            onClick={handleSaveAndTurnOff}
                            disabled={saveMappedResourcesLoading}
                          >
                            Save & Turn Off
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Hover prompt to enable simulation mode */}
                  {!sim_flag && !readOnly && (
                    <div className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20">
                      Turn on simulation mode to assign people
                    </div>
                  )}

                  <div className="space-y-3">
                    {roadmapDetails?.skills_required && roadmapDetails?.skills_required.map((skill: any, index: number) => {
                      // Check multiple possible keys for members data
                      const rawSkillMembers = mappedResource[skill.id]?.members ||
                        mappedResource[skill.name]?.members ||
                        mappedResource[skill.skill_id]?.members ||
                        [];

                      // Remove duplicates based on member ID
                      const skillMembers = Array.from(
                        new Map(rawSkillMembers.map((member: any) => [member.id, member])).values()
                      );

                      const assignedCount = skillMembers.length;
                      const capacityPercent = roadmapDetails?.skill_capacity_percent?.[skill?.name] || 0;

                      return (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200 group" key={`${skill.name}_${index}`} data-skill-id={skill.id}>
                          <div className="flex flex-col gap-4">
                            {/* Header Row */}
                            <div className="flex items-center justify-between">
                              {/* Skill Info */}
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-10 h-10 rounded-lg relative flex items-center justify-center text-white font-medium text-sm"
                                  style={{ backgroundColor: skill?.skill_color }}
                                >
                                  {skill?.name?.slice(0, 2).toUpperCase()}
                                </div>
                                <div className="ml-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-bold text-gray-900 dark:text-white text-base">
                                      {skill?.name}
                                    </h4>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${capacityPercent > 80
                                      ? 'bg-red-200 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                      : capacityPercent > 50
                                        ? 'bg-orange-200 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                        : 'bg-green-200 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                      }`}>
                                      {Math.round(capacityPercent)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1">
                                    <div className="flex items-center gap-1">
                                      <div className="w-20 bg-gray-300 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                        <div
                                          className="h-full rounded-full transition-all duration-500"
                                          style={{
                                            width: `${(assignedCount / skill.count) * 100}%`,
                                            backgroundColor: skill?.skill_color
                                          }}
                                        />
                                      </div>
                                      <span className="text-xs text-gray-700 dark:text-gray-400 font-medium">
                                        {assignedCount}/{skill.count}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>


                            </div>

                            {/* Members Section */}
                            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-300 dark:border-gray-700">
                              {/* Existing Members */}
                              {skillMembers.map((member: any, memberIndex: number) => {
                                const initials = member?.name?.split(' ')
                                  .map((n: string) => n?.[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2) || "??";

                                return (
                                  <div
                                    key={`${skill.id}-${member.id}-${memberIndex}`}
                                    className="relative flex items-center gap-2 bg-white dark:bg-gray-700 rounded-md px-3 py-2 border border-gray-200 dark:border-gray-600 group/member transition-all duration-200"
                                  >
                                    {/* Error Popover */}
                                    {assignmentError?.skillId === skill.id && assignmentError?.memberId === member.id && (
                                      <div className="assignment-error-popover absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-red-50 dark:bg-red-900/90 text-red-800 dark:text-red-100 text-xs p-3 rounded-lg shadow-xl border border-red-200 dark:border-red-700 z-[9999] animate-in fade-in zoom-in-95 duration-200">
                                        <div className="font-bold mb-1 flex items-center gap-1">
                                          <HiExclamation className="w-4 h-4" />
                                          Cannot Remove
                                        </div>
                                        <div className="mb-1 opacity-90">Already assigned to this task:</div>
                                        <ul className="list-disc pl-4 mb-1 opacity-90 leading-tight max-h-24 overflow-y-auto">
                                          {assignmentError.taskNames.length > 0 ? (
                                            assignmentError.taskNames.map((t, i) => <li key={i}>{t}</li>)
                                          ) : (
                                            <li>Unknown tasks</li>
                                          )}
                                        </ul>
                                        <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-red-50 dark:bg-red-900/90 border-b border-r border-red-200 dark:border-red-700 transform rotate-45"></div>

                                        <button
                                          className="absolute top-1 right-1 p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-full text-red-600 dark:text-red-300"
                                          onClick={(e) => { e.stopPropagation(); setAssignmentError(null); }}
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                      </div>
                                    )}

                                    {/* Avatar using Resources.tsx style */}
                                    <div className="relative w-8 h-8 rounded-full border-2 border-gray-400 dark:border-gray-600">
                                      {member.image_url ? (
                                        <img
                                          src={member.image_url}
                                          alt={member?.name}
                                          className="w-full h-full rounded-full object-cover"
                                          onError={(e) => {
                                            // Fallback to initials if image fails to load
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            const parent = target.parentElement;
                                            if (parent) {
                                              parent.innerHTML = `<div class="w-full h-full rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-800 dark:text-white text-sm font-medium">${initials}</div>`;
                                            }
                                          }}
                                        />
                                      ) : (
                                        <div className="w-full h-full rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-800 dark:text-white text-sm font-medium">
                                          {initials}
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                                      {member?.name || "Unknown"}
                                    </span>
                                    {sim_flag && !readOnly && (
                                      <button
                                        className="opacity-0 group-hover/member:opacity-100 ml-1 p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-600 transition-all duration-200"
                                        onClick={() => handleMemberRemove(member, skill.id)}
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                );
                              })}

                              {/* Dotted Add Member Boxes for remaining slots */}
                              {sim_flag && !readOnly && Array.from({ length: skill.count - assignedCount }).map((_, emptyIndex) => (
                                <div key={`empty-${skill.id}-${emptyIndex}`} className="relative">
                                  <button
                                    className="flex items-center gap-2 bg-transparent rounded-md px-2 py-1.5 border border-dashed border-gray-400 dark:border-gray-500 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 group/add add-member-btn"
                                    data-button-index={emptyIndex}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddMemberClick(skill.id, emptyIndex);
                                    }}
                                  >
                                    {/* Dotted Avatar Placeholder */}
                                    <div className="relative w-7 h-7 rounded-full border border-dashed border-gray-400 dark:border-gray-500 group-hover/add:border-blue-500 dark:group-hover/add:border-blue-400 flex items-center justify-center transition-colors duration-200">
                                      <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover/add:text-blue-500 dark:group-hover/add:text-blue-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                                      </svg>
                                    </div>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 group-hover/add:text-blue-600 dark:group-hover/add:text-blue-400 font-medium transition-colors duration-200">
                                      Add Person
                                    </span>
                                  </button>

                                  {/* Inline Member Dropdown */}
                                  {showMemberDropdown && selectedSkill === skill.id && selectedButtonIndex === emptyIndex && (
                                    <div className={`absolute left-0 mt-2 w-96 bg-white dark:bg-gray-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-50 max-h-80 overflow-hidden member-dropdown ${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full'
                                      }`}>


                                      {/* Search Bar */}
                                      <div className="p-1 pr-3 border-b flex items-center justify-between border-gray-200 dark:border-gray-600">
                                        <div className="relative ">
                                          <input
                                            type="text"
                                            placeholder="Search people..."
                                            className="w-full pl-9 pr-3 py-2 text-sm bg-transparent border-none   text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0  focus:border-transparent"
                                            value={memberSearchQuery}
                                            onChange={(e) => setMemberSearchQuery(e.target.value)}
                                          />
                                          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                          </svg>
                                        </div>
                                        <button
                                          onClick={handleCloseMemberDropdown}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>

                                      </div>

                                      <div className="max-h-64 overflow-y-auto">
                                        {skill_resources_Loading ? (
                                          <div className="flex items-center justify-center p-8">
                                            <div className="flex flex-col items-center gap-2">
                                              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                              <p className="text-xs text-gray-500 dark:text-gray-400">Loading people...</p>
                                            </div>
                                          </div>
                                        ) : filteredAvailableMembers.length > 0 ? (
                                          filteredAvailableMembers.map((member: any) => {
                                            const initials = member?.name?.split(' ')
                                              .map((n: string) => n?.[0])
                                              .join('')
                                              .toUpperCase()
                                              .slice(0, 2) || "??";

                                            // Check if already assigned to avoid duplicates
                                            const isAlreadyAssigned = mappedResource[skill.id]?.members?.some((m: any) => m.id === member.id);

                                            if (isAlreadyAssigned) return null;

                                            return (
                                              <div
                                                key={member.id}
                                                className="p-3 border-b border-gray-100 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors duration-200 last:border-b-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSelectMember(member);
                                                }}
                                              >
                                                <div className="flex items-start gap-3">
                                                  <div className="relative w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-500 flex-shrink-0">
                                                    {member.image_url ? (
                                                      <img
                                                        src={member.image_url}
                                                        alt={member?.name}
                                                        className="w-full h-full rounded-full object-cover"
                                                        onError={(e) => {
                                                          const target = e.target as HTMLImageElement;
                                                          target.style.display = 'none';
                                                          const parent = target.parentElement;
                                                          if (parent) {
                                                            parent.innerHTML = `<div class="w-full h-full rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-white text-sm font-medium">${initials}</div>`;
                                                          }
                                                        }}
                                                      />
                                                    ) : (
                                                      <div className="w-full h-full rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-white text-sm font-medium">
                                                        {initials}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                      <h5 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                                        {member?.name || "No Name"}
                                                      </h5>
                                                      <div className="text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                      </div>
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                      {member?.role || "Team Member"}
                                                    </p>

                                                    {/* Current Projects Count */}
                                                    {member.projects_info && member.projects_info.length > 0 ? (
                                                      <div className="mt-2 flex items-center gap-1">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                        <CustomTooltip
                                                          placement="top"
                                                          title={
                                                            <div className="max-w-xs">
                                                              <div className="font-medium text-xs mb-1">Currently working on:</div>
                                                              {member.projects_info.map((project: any, idx: number) => (
                                                                <div key={project.project_id} className="text-xs py-0.5">
                                                                  • {project.project_name} ({project?.working_done || 0}% complete)
                                                                </div>
                                                              ))}
                                                            </div>
                                                          }
                                                        >
                                                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium cursor-help border-b border-dashed border-blue-400 dark:border-blue-500">
                                                            {member.projects_info.length} project{member.projects_info.length !== 1 ? 's' : ''}
                                                          </span>
                                                        </CustomTooltip>
                                                      </div>
                                                    ) : (
                                                      <div className="mt-2 flex items-center gap-1">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                          Available Now
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <div className="p-6 text-center">
                                            <svg className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                              {memberSearchQuery.trim()
                                                ? `No people found matching "${memberSearchQuery}"`
                                                : "No available people for this skill"
                                              }
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}

                              {/* No members message when no members and not simulation mode */}
                              {skillMembers.length === 0 && !sim_flag && (
                                <div className="text-center py-3 text-gray-500 dark:text-gray-400 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 w-full">
                                  No people assigned yet
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer>

    </>
  );
};

export default SkillsModal;